import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/affine/store';

/**
 * Analyze-result block — Phase E custom BlockSuite block that wraps
 * an analyze-mode AnalyzeResult + DatasetProfile + Capsule into a
 * single interactive card. Replaces the markdown-only rendering for
 * the parts that benefit from real DOM (download button, hover
 * tooltips on the chart, etc.) while keeping the plain markdown for
 * narrative sections.
 *
 * The block stores both shapes (`analyzeResult`, `datasetProfile`)
 * as serialised JSON strings so BlockSuite's Y.Doc CRDT can diff
 * them as single atoms — the doc-writer never mutates the same
 * block twice in a turn, so we don't need finer-grained tracking.
 */
type AnalyzeResultProps = {
  /**
   * Serialised `AnalyzeResult` (statistics, plots, code, methodCitation,
   * caveats, suggestedNextSteps, methodsParagraph, capsule, …).
   * JSON string. Always non-empty when the block is created.
   */
  analyzeResultJson: string;
  /**
   * Serialised `DatasetProfile` (preview, kind, signals,
   * suggestedAnalyses, proposedPipeline). JSON string. May be empty
   * for analyses run from session cache where we no longer have the
   * profile in scope.
   */
  datasetProfileJson: string;
};

export const AnalyzeResultBlockSchema = defineBlockSchema({
  flavour: 'affine:analyze-result',
  props: (): AnalyzeResultProps => ({
    analyzeResultJson: '',
    datasetProfileJson: '',
  }),
  metadata: {
    version: 1,
    role: 'content',
    children: [],
  },
  toModel: () => new AnalyzeResultBlockModel(),
});

export const AnalyzeResultBlockSchemaExtension = BlockSchemaExtension(
  AnalyzeResultBlockSchema
);

export class AnalyzeResultBlockModel extends BlockModel<AnalyzeResultProps> {}
